/*
  Порядок загрузки лентой шагов - макет владельца 30.09.2026.

  До этого дня раздел был шестью равноправными вкладками, и порядок из них не
  читался: заказчик грузил отчёт по начислениям первым, счетов в портфеле не
  возникало, и приходилось объяснять голосом, с чего начинать.

  Шаги отвечают на разные вопросы, и потому их четыре:
    0 - срок внесения платы: он из договора управления, и без него не считаются
        ни пени, ни исковая давность. Стоит ПЕРВЫМ намеренно - загрузка читает
        настройку в момент записи, и заполненный позже срок к уже загруженным
        начислениям сам не проставится;
    1 - реестр должников: кто и где. Заводит лицевые счета и должников;
    2 - начисления по месяцам: сколько и за что. Из них считается всё остальное;
    3 - домовая книга: кто зарегистрирован в помещении. Её печатает выписка
        из лицевого счёта - приложение к заявлению.

  🔴 Шаг 3 переименован и доделан 30.09.2026 по указанию владельца. До этого дня
  он назывался «Финансово-лицевой счёт» и честно говорил, что на вход не
  принимается: бланк управляющей компании приходит сканом. Сам лист система
  формирует с 18.09.2026, и из него ей не хватало ровно одного - состава
  зарегистрированных, который до сих пор приходил только ночным обменом с 1С.
  Его и принимает шаг: не бланк, а те сведения из бланка, которых нет больше
  нигде. Площади грузит вкладка «Помещения», паспорта - «Идентификаторы».

  Лента - переключатель, а не картинка: шаг выбирается нажатием. На узком экране
  подписи уходят, остаётся полоса с номерами: название текущего шага и так стоит
  заголовком под ней (проверено замером на 390 - иначе тридцать пикселей
  горизонтальной прокрутки).

  🔴 Лента ЗАПОЛНЯЕТСЯ по мере готовности данных (указание владельца
  30.09.2026): загруженный шаг залит, и цифра в нём горит. До этой правки
  закрашивался только шаг 0, а шаги 1 и 2 оставались серыми навсегда - сколько
  бы файлов человек ни загрузил, лента показывала лишь то, где он стоит сейчас.

  Состояний три, а не два, и третье - не придирка. Сделанным шаг зовётся при
  полном покрытии портфеля; есть данные по части счетов - шаг помечен начатым.
  Начисления по 45 счетам из 221 с зелёным кружком означали бы «здесь всё
  хорошо» там, где по остальным 176 заявление не соберётся.

  🔴 Линия к шагу залита ЦЕЛИКОМ, если по шагу есть данные (решение владельца
  06.10.2026: «нажали кнопку на шаге, которая завела данные, - линия полностью
  зелёная, шаг сделан»). До этого с 01.10.2026 линия заливалась на долю
  покрытия, а ещё раньше - наполовину; оба раза частичная заливка читалась не
  как «сколько загружено», а как кусок соседнего шага. Сколько именно счетов
  покрыто, говорят кружок (светлый - не все) и подпись под ним.

  🔴 Файлы шага показываются ПОД шагом и только его (указание владельца
  01.10.2026: «на каждом шаге должны быть файлы только этого шага, иначе
  определять, загрузили файл в шаг, нельзя»). До этого дня журнал был один на
  весь раздел и стоял внизу страницы: реестра и домовой книги в нём не было
  вовсе, а выгрузки начислений лежали вперемешку с выпиской.
*/

import { useState } from 'react'

import { accounts as accountsWord } from '../api/format.js'
import { Lock } from './AccessContext.jsx'
import OneCUpload from './OneCUpload.jsx'
import RegistryUpload from './RegistryUpload.jsx'
import ResidentsUpload from './ResidentsUpload.jsx'
import StepJournal from './StepJournal.jsx'

const DUE_DAY = 'due-day'
const REGISTRY = 'registry'
const CHARGES = 'charges'
const RESIDENTS = 'residents'

/* Шаг ленты - требование чек-листа готовности. Состояние берётся оттуда, а не
   из журнала загрузок и не своим счётом: чек-лист считается на сервере тем же
   перечнем, которым проверяет себя сборщик пакета (`data_readiness_service`), и
   второй счёт в кабинете обещал бы готовность там, где документ не собирается.
   Реестр отвечает за субъектов права («кто и где») - поэтому `owners`. */
const REQUIREMENTS = { [REGISTRY]: 'owners', [CHARGES]: 'charges', [RESIDENTS]: 'residents' }

const STEP_STATES = { ready: 'done', partial: 'part', missing: 'none' }

/* Вид файла в журнале загрузок - тот же признак, которым сервер отбирает строки
   шага. У шага «Срок внесения платы» файла нет вовсе: он задаётся в настройках
   организации, и журналу показывать нечего. */
const STEP_KINDS = { [REGISTRY]: 'registry', [CHARGES]: 'charges', [RESIDENTS]: 'residents' }

/* Залита ли линия к шагу. 🔴 Решение владельца 06.10.2026: линия зеленеет
   целиком, как только по шагу загружены данные - сколько бы их ни было: файл
   загружен, значит шаг сделан. Покрытие («16 счетов из 216») говорят светлый
   кружок и подпись под ним. Заливка на долю покрытия (01.10.2026) снята: на
   снимке владельца 16 счетов из 216 давали короткий зелёный обрубок, который
   читался как начало следующего шага. */
function fillOf(state) {
  return state === 'none' ? '0%' : '100%'
}

/* Покрытие числом рядом с цветом. Цвет отвечает «сделано или нет», число -
   «сколько именно»: покрытие по одному дому и по всему портфелю выглядят
   одинаково, а пакет из них собирается по-разному. */
function coverage(row) {
  if (!row || !row.total) return null
  if (!row.covered) return 'пока ни по одному'
  return `${accountsWord(row.covered)} из ${row.total}`
}

function requirement(readiness, step) {
  return readiness?.items.find((item) => item.key === REQUIREMENTS[step]) ?? null
}

function stateOf(row) {
  return STEP_STATES[row?.state] ?? 'none'
}

/* Шаг, в который грузят файл: состояние и заливка берутся из одной строки
   чек-листа, а не считаются в двух местах по-разному. */
function fileStep(key, title, fallback, readiness) {
  const row = requirement(readiness, key)
  const state = stateOf(row)
  return { key, title, note: coverage(row) ?? fallback, state, fill: fillOf(state) }
}

function steps(dueDay, readiness) {
  return [
    {
      key: DUE_DAY,
      title: 'Срок внесения платы',
      note: dueDay ? `задан: ${dueDay} число` : 'не задан',
      /* Срок берётся не из чек-листа: там он считается вместе с покрытием
         начислений, а шаг отвечает на простой вопрос - задан он или нет. */
      state: dueDay ? 'done' : 'none',
      fill: dueDay ? '100%' : '0%',
    },
    fileStep(REGISTRY, 'Реестр должников', 'кто и где', readiness),
    fileStep(CHARGES, 'Начисления по месяцам', 'сколько и за что', readiness),
    /* Названо по документу, а не по данным (указание владельца 30.09.2026):
       «домовая книга» - то, что человек держит в руках и ищет у себя, а
       «состав зарегистрированных» - как эти сведения называются у нас внутри. */
    fileStep(RESIDENTS, 'Домовая книга', 'кто живёт в помещении', readiness),
  ]
}

function Flow({ items, active, onPick }) {
  return (
    <ol className="flow">
      {items.map((step) => (
        <li
          key={step.key}
          className="flow__item"
          /* Состояние и текущее место - атрибутами, а не парой классов:
             оформление цепляется за них же, и у «сделано» с «выбрано сейчас»
             остаётся один источник правды. */
          data-state={step.state}
          data-current={step.key === active ? 'yes' : undefined}
          /* Заливка линии - переменной оформления: её же читает тест, а цвет
             виден только в браузере. */
          style={{ '--flow-fill': step.fill }}
        >
          <button
            type="button"
            /* `no-hover-lift`: шаг разметкой кнопка, но для человека это
               переключатель, а не действие - то же исключение, что у вкладки. */
            className="flow__pick no-hover-lift"
            aria-current={step.key === active ? 'step' : undefined}
            onClick={() => onPick(step.key)}
          >
            <span className="flow__dot">{items.indexOf(step)}</span>
            <span className="flow__title">{step.title}</span>
            <span className="flow__note">{step.note}</span>
          </button>
        </li>
      ))}
    </ol>
  )
}

export default function ImportSteps({ dueDay, readiness, onLoaded, onEditDueDay }) {
  const [step, setStep] = useState(REGISTRY)

  /* Загрузка, строку которой журнал шага обязан дождаться. Сбрасывается при
     переходе на другой шаг: там этой строки нет и не будет, а журнал ждал бы её
     четырьмя лишними обращениями к серверу. */
  const [loaded, setLoaded] = useState(null)

  const items = steps(dueDay, readiness)
  const current = items.find((item) => item.key === step)

  function pick(next) {
    setLoaded(null)
    setStep(next)
  }

  async function loadedHere(runId) {
    setLoaded(runId ?? null)
    await onLoaded(runId)
  }

  return (
    <>
      <Flow items={items} active={step} onPick={pick} />

      <h2 className="section__title">
        Шаг {items.indexOf(current)}. {current.title}
      </h2>

      {step === DUE_DAY ? (
        <section className="section card">
          <p>
            Срок внесения платы берётся из договора управления и задаётся один раз, в настройках
            организации. Сейчас он {dueDay ? <b>{dueDay} число месяца</b> : <b>не задан</b>}.
          </p>
          <p className="muted">
            Заполните его <b>до</b> загрузки начислений: срок проставляется в момент записи. Если
            загрузить сначала, а срок указать потом, у загруженных начислений он останется пустым -
            и пени с исковой давностью по ним не посчитаются, пока выгрузку не повторят.
          </p>
          <div className="row">
            <button type="button" className="button" onClick={onEditDueDay}>
              Задать срок в настройках
            </button>
          </div>
        </section>
      ) : null}

      {/* Загрузка при неоплаченной лицензии закрыта (аудит 26.10.2026, причина 2); журнал
          под ней остаётся рабочим - его листание вне `Lock`. */}
      {step === REGISTRY ? (
        <Lock>
          <RegistryUpload onLoaded={loadedHere} />
        </Lock>
      ) : null}

      {step === CHARGES ? (
        <Lock>
          <OneCUpload dueDay={dueDay} onLoaded={loadedHere} onEditDueDay={onEditDueDay} />
        </Lock>
      ) : null}

      {step === RESIDENTS ? (
        <>
          <p className="muted">
            Это последняя часть финансово-лицевого счёта, которой Арбитру ЖКХ не хватает: сам лист
            он формирует сам, площади берёт из вкладки «Помещения», паспорта - из вкладки
            «Идентификаторы», а кто зарегистрирован в помещении, знает обмен с 1С. Пришлите список
            жильцов таблицей или скан выписки из домовой книги - со скана Арбитр ЖКХ вычитает
            жильцов сам, а вы сверите прочитанное с картинкой постранично.
          </p>
          <Lock>
            <ResidentsUpload onLoaded={loadedHere} />
          </Lock>
        </>
      ) : null}

      {/* Файлы этого шага - и только его. `key` по шагу: при переходе журнал
          начинается с чистого листа, а не показывает мгновение чужие строки. */}
      {STEP_KINDS[step] ? (
        <StepJournal key={step} kind={STEP_KINDS[step]} awaiting={loaded} />
      ) : null}
    </>
  )
}
